import React, { useState } from 'react';
import AnimatedLogo from './AnimatedLogo';

interface Props { onLogin: (session: any) => void; }

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem('ce_demo_theme') !== 'light');
  const toggle = () => setDark(d => {
    const next = !d;
    localStorage.setItem('ce_demo_theme', next ? 'dark' : 'light');
    return next;
  });
  return { dark, toggle };
}

function tok(dark: boolean) {
  return dark ? {
    bg: '#000000', card: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.08)',
    text: '#f8fafc', muted: '#94a3b8', dim: '#64748b',
    input: 'rgba(255,255,255,0.05)', inputBorder: 'rgba(255,255,255,0.12)',
    navBg: 'rgba(0,0,0,0.8)', gridLine: 'rgba(99,102,241,0.05)',
    shadow: '0 32px 80px rgba(0,0,0,0.6)',
    btnSecBg: 'rgba(255,255,255,0.06)', btnSecBorder: 'rgba(255,255,255,0.1)',
  } : {
    bg: '#f0f4ff', card: 'rgba(255,255,255,0.85)', border: 'rgba(15,23,42,0.1)',
    text: '#0f172a', muted: '#475569', dim: '#64748b',
    input: 'rgba(255,255,255,0.8)', inputBorder: 'rgba(15,23,42,0.15)',
    navBg: 'rgba(240,244,255,0.9)', gridLine: 'rgba(99,102,241,0.08)',
    shadow: '0 8px 32px rgba(15,23,42,0.12)',
    btnSecBg: 'rgba(15,23,42,0.06)', btnSecBorder: 'rgba(15,23,42,0.15)',
  };
}

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.94a16 16 0 0 0 6.06 6.06l1.06-1.06a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7a2 2 0 0 1 1.72 2.03z"/>
  </svg>
);
const SunIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);
const MoonIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);
const EyeIcon = ({ open }: { open: boolean }) => open ? (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
) : (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    <polyline points="9 12 11 14 15 10" strokeWidth="2"/>
  </svg>
);

export default function AdminDemoGate({ onLogin }: Props) {
  const { dark, toggle } = useTheme();
  const t = tok(dark);
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPass, setShowPass] = useState(false);

  const gridBg = `linear-gradient(${t.gridLine} 1px, transparent 0), linear-gradient(90deg, ${t.gridLine} 1px, transparent 0)`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const authRes  = await fetch('/api/demo/auth', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, mode: 'admin' }),
      });
      const authData = await authRes.json().catch(() => ({}));
      if (!authRes.ok) { setError(authData.error || 'Invalid admin password.'); setLoading(false); return; }

      const loginRes  = await fetch('/api/demo/instant-login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: 'admin', demo_token: authData.demo_token }),
      });
      const loginData = await loginRes.json().catch(() => ({}));
      if (!loginRes.ok) { setError(loginData.error || 'Login failed.'); setLoading(false); return; }

      localStorage.setItem('token', loginData.token);
      const sess = { token: loginData.token, user: loginData.user, sipPassword: null, sipUsername: null, dids: [] };
      localStorage.setItem('ce_session', JSON.stringify(sess));
      onLogin(sess);
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh', background: t.bg,
      backgroundImage: gridBg, backgroundSize: '64px 64px',
      fontFamily: 'Inter, system-ui, sans-serif',
      display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position:'absolute', top:'-10%', left:'50%', transform:'translateX(-50%)', width:'80%', height:'60%', borderRadius:'50%', pointerEvents:'none', background:'radial-gradient(ellipse at center, rgba(99,102,241,0.18) 0%, transparent 65%)' }} />

      {/* Nav */}
      <nav style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 36px', background:t.navBg, backdropFilter:'blur(20px)', borderBottom:`1px solid ${t.border}`, position:'relative', zIndex:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:9 }}>
          <AnimatedLogo width={40} />
            <div style={{ lineHeight: 1.3 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: 'rgba(255,255,255,0.92)', letterSpacing: 0.2, fontFamily: 'Inter, Arial, sans-serif' }}>
                CallEssential
              </div>
              <div style={{ fontSize: 9.5, fontWeight: 500, color: 'rgba(255,255,255,0.45)', letterSpacing: 0.3, fontFamily: 'Inter, Arial, sans-serif' }}>
                powered by Synergytalk
              </div>
            </div>
          <span style={{ marginLeft:6, fontSize:10, fontWeight:500, color:'#a5b4fc', background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.25)', borderRadius:999, padding:'2px 8px', letterSpacing:'0.05em', textTransform:'uppercase' }}>Admin Demo</span>
        </div>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <a href="/" style={{ fontSize:13, color:t.muted, textDecoration:'none', fontWeight:500, padding:'7px 14px', borderRadius:8, background:t.btnSecBg, border:`1px solid ${t.btnSecBorder}` }}>← Demo Hub</a>
          <button onClick={toggle} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:8, background:t.btnSecBg, border:`1px solid ${t.border}`, color:t.muted, cursor:'pointer', fontSize:12, fontWeight:500 }}>
            {dark ? <SunIcon /> : <MoonIcon />}{dark ? 'Light' : 'Dark'}
          </button>
        </div>
      </nav>

      {/* Card */}
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 20px', position:'relative', zIndex:5 }}>
        <div style={{ width:'100%', maxWidth:420, background:t.card, backdropFilter:'blur(20px)', border:`1px solid ${t.border}`, borderRadius:20, padding:'44px 40px', boxShadow:t.shadow }}>
          <div style={{ textAlign:'center', marginBottom:32 }}>
            <div style={{ width:68, height:68, borderRadius:18, margin:'0 auto 18px', background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.25)', display:'flex', alignItems:'center', justifyContent:'center', color:'#818cf8' }}>
              <ShieldIcon />
            </div>
            <div style={{ display:'inline-block', marginBottom:14, background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.28)', borderRadius:999, padding:'4px 14px', fontSize:11, color:'#a5b4fc', fontWeight:500, letterSpacing:'0.06em', textTransform:'uppercase' }}>Restricted Access</div>
            <div style={{ fontSize:22, fontWeight:700, color:t.text, marginBottom:6 }}>Admin Panel</div>
            <div style={{ fontSize:13, color:t.muted }}>Enter your admin credentials to continue</div>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom:24 }}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, color:t.muted, marginBottom:7, textTransform:'uppercase', letterSpacing:'0.05em' }}>Admin Password</label>
              <div style={{ position:'relative' }}>
                <input
                  type={showPass ? 'text' : 'password'} required autoComplete="current-password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  style={{ width:'100%', padding:'11px 44px 11px 14px', borderRadius:10, background:t.input, border:`1px solid ${t.inputBorder}`, color:t.text, fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                />
                <button type="button" onClick={() => setShowPass(v => !v)} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:t.muted, padding:0 }}>
                  <EyeIcon open={showPass} />
                </button>
              </div>
            </div>

            {error && (
              <div style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#fca5a5', textAlign:'center' }}>{error}</div>
            )}

            <button type="submit" disabled={loading} style={{ width:'100%', padding:'13px', background: loading ? 'rgba(99,102,241,0.5)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', border:'none', borderRadius:10, color:'#fff', fontSize:14, fontWeight:600, cursor: loading ? 'not-allowed' : 'pointer', transition:'all 0.2s', fontFamily:'inherit' }}>
              {loading ? 'Signing in…' : 'Access Admin Panel →'}
            </button>
          </form>

          <div style={{ marginTop:24, textAlign:'center', borderTop:`1px solid ${t.border}`, paddingTop:18 }}>
            <p style={{ fontSize:12, color:t.dim, margin:0 }}>
              <a href="/" style={{ color:'#818cf8', textDecoration:'none' }}>← Back to Demo Hub</a>
              {' · '}
              <a href="mailto:sales@synergytalk.ae" style={{ color:'#818cf8', textDecoration:'none' }}>Contact sales</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
